import { TileLayer, useMap } from 'react-leaflet';
import { useEffect, useState, useRef } from 'react';
import type { TileErrorEvent } from 'leaflet';
import { useThemeStore } from '../../store/themeStore';

/**
 * Base map tiles.
 *
 * One raster source in both modes: OpenStreetMap (DE mirror; FR mirror as
 * fallback after repeated tile errors). The coloured map is the default look;
 * the optional "Dunkle Karte" (browser-local setting) renders the very same
 * tiles through a CSS filter (`.industrial-tiles`, see leaflet-fixes.css) as a
 * dark, desaturated, low-contrast backdrop — no second provider, no API key
 * (CARTO's free basemaps started serving "API KEY REQUIRED" tiles in 2026).
 *
 * Both mirrors serve tiles up to zoom 20 and answer 404/422 above that. The
 * map allows zoom 22, so `maxNativeZoom` MUST stay at 20: Leaflet then scales
 * the z20 tiles instead of requesting tiles that do not exist. A failed tile
 * counts as "loaded" for Leaflet, its parent tile gets pruned and the map
 * background shows through — that was the black-tile effect at high zoom.
 *
 * Sporadic errors (rate limiting, dropped connections) are retried once per
 * tile with a cache-buster before they count towards the mirror fallback.
 */

const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
/** Highest zoom both OSM mirrors actually serve (verified 2026-09: 404/422 at z21). */
const OSM_MAX_NATIVE_ZOOM = 20;
const MAP_MIN_ZOOM = 8;
const MAP_MAX_ZOOM = 22;
const MAX_ERRORS_BEFORE_FALLBACK = 3;
const TILE_RETRY_DELAY_MS = 1200;
const TILE_RETRY_ATTR = 'data-retry';

export function RedundantTileLayers() {
  const map = useMap();
  const darkMapTiles = useThemeStore(state => state.darkMapTiles);
  const [useFallback, setUseFallback] = useState(false);
  const primaryErrorCount = useRef(0);

  useEffect(() => {
    primaryErrorCount.current = 0;
    const pendingRetries = new Set<number>();

    const handleTileError = (event: TileErrorEvent) => {
      const tile = event.tile as HTMLImageElement;
      // First failure: retry the same tile once. Chrome remembers a failed image
      // load for the document, so the retry needs a cache-busting query.
      if (!tile.getAttribute(TILE_RETRY_ATTR)) {
        tile.setAttribute(TILE_RETRY_ATTR, '1');
        const originalSrc = tile.src;
        const timer = window.setTimeout(() => {
          pendingRetries.delete(timer);
          if (tile.isConnected && tile.src === originalSrc) {
            tile.src = `${originalSrc}${originalSrc.includes('?') ? '&' : '?'}retry=1`;
          }
        }, TILE_RETRY_DELAY_MS);
        pendingRetries.add(timer);
        return;
      }

      primaryErrorCount.current += 1;
      if (primaryErrorCount.current >= MAX_ERRORS_BEFORE_FALLBACK) {
        setUseFallback(true);
      }
    };

    map.on('tileerror', handleTileError);

    return () => {
      map.off('tileerror', handleTileError);
      pendingRetries.forEach(timer => window.clearTimeout(timer));
    };
  }, [map]);

  // Container background for the gaps between tiles (see leaflet-fixes.css).
  useEffect(() => {
    const container = map.getContainer();
    container.classList.toggle('industrial-map', darkMapTiles);
    return () => {
      container.classList.remove('industrial-map');
    };
  }, [map, darkMapTiles]);

  const layerClass = darkMapTiles ? 'primary-layer industrial-tiles' : 'primary-layer';
  const fallbackClass = darkMapTiles ? 'fallback-layer industrial-tiles' : 'fallback-layer';

  // The key includes the mode so a mode switch remounts the layer container:
  // Leaflet only applies `className` when the container is created.
  return (
    <>
      {!useFallback && (
        <TileLayer
          key={`primary-${darkMapTiles ? 'dark' : 'light'}`}
          attribution={OSM_ATTRIBUTION}
          url="https://{s}.tile.openstreetmap.de/{z}/{x}/{y}.png"
          maxZoom={MAP_MAX_ZOOM}
          maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
          minZoom={MAP_MIN_ZOOM}
          subdomains={['a', 'b', 'c']}
          className={layerClass}
          keepBuffer={3}
          updateWhenIdle={false}
          updateWhenZooming={false}
        />
      )}

      {useFallback && (
        <TileLayer
          key={`fallback-${darkMapTiles ? 'dark' : 'light'}`}
          attribution={OSM_ATTRIBUTION}
          url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
          maxZoom={MAP_MAX_ZOOM}
          maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
          minZoom={MAP_MIN_ZOOM}
          subdomains={['a', 'b', 'c']}
          className={fallbackClass}
          keepBuffer={3}
          updateWhenIdle={false}
          updateWhenZooming={false}
        />
      )}
    </>
  );
}
